/** @file Derives a puppet's image slots: one per (impostor, facet entry, drawn tile) (design 4.3, 4.6, 17.1). */
import { slotKey } from '@zilo/pose3d-format/derive/slotKey';
import { resolveSectorTable } from '@zilo/pose3d-format/tables/resolveSectorTable';
import { sectorCentre } from '@zilo/pose3d-format/tables/sectorCentre';
import { sectorOf } from '@zilo/pose3d-format/tables/sectorOf';
import { tileCountOf } from '@zilo/pose3d-format/tables/tileCountOf';
import type { FacetEntry, ImageSlot, SectorTable } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';

/**
 * For each tile drawn only through `flip`, the tile shown unflipped at the mirrored azimuth: the flipped image
 * of tile t at azimuth a equals the mirror of the view at 360 - a, so t repeats that tile.
 */
function mirrorSources(table: SectorTable): Map<number, number> {
  const sources = new Map<number, number>();
  const plain = new Set(table.tile.filter((t, s) => t >= 0 && table.flip[s] !== true));
  table.tile.forEach((t, s) => {
    if (t < 0 || plain.has(t) || sources.has(t)) return;
    const m = sectorOf(360 - sectorCentre(table, s), table);
    const source = table.tile[m] ?? -1;
    if (source >= 0 && source !== t && table.flip[m] !== true) sources.set(t, source);
  });
  return sources;
}

function slotsOf(drawable: string, index: number, entry: FacetEntry, table: SectorTable): ImageSlot[] {
  const sources = mirrorSources(table);
  const [w, h] = entry.size;
  return Array.from({ length: tileCountOf(table) }, (_, tile): ImageSlot => {
    const source = sources.get(tile);
    const azimuth = entry.drawnAzimuths?.[tile];
    const screen = entry.drawnScreenAngles?.[tile];
    return {
      key: slotKey(drawable, index, tile), drawable, entry: index, tile, size: entry.size,
      pivot: [entry.pivotFrac[0] * w, entry.pivotFrac[1] * h],
      ...(azimuth === undefined ? {} : { drawnAzimuth: azimuth }),
      ...(screen === undefined ? {} : { drawnScreenAngle: screen }),
      bands: entry.bands, frames: entry.frames, variants: entry.variants,
      ...(source === undefined ? {} : { mirroredFrom: slotKey(drawable, index, source) }),
      status: Object.hasOwn(entry.slots, String(tile)) ? 'filled' : 'empty',
    };
  });
}

/** Slots of every impostor of a validated host puppet (flattened attachments included), by drawable, entry, tile. */
export function deriveSlots(puppet: Puppet): ImageSlot[] {
  return puppet.drawables.flatMap((d) => {
    if (d.kind !== 'impostor') return [];
    return d.facets.flatMap((entry, i) => {
      const table = resolveSectorTable(entry.yaw, puppet);
      return table === undefined ? [] : slotsOf(d.id, i, entry, table);
    });
  });
}
